import { useEffect, useMemo, useState } from "react";
import { ScrollText, Eye, ShieldAlert, ArrowRight, RefreshCw, Search, Download, Loader2, ChevronDown, ChevronRight } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { downloadCSV } from "@/lib/csv";
import AdminNav from "@/components/AdminNav";

const fmtDT = (d) => (d ? new Date(d).toLocaleString("es-CL", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

// Etiquetas humanas para las acciones más comunes
const ACTION_LABELS = {
  view_professional_detail: "Vio ficha profesional sensible",
  document_uploaded: "Subió documento",
  status_transition: "Cambio de estado",
  payment_released: "Liberó pago",
  approve: "Aprobó verificación",
  reject: "Rechazó verificación",
  delete_expense: "Eliminó gasto",
};

function actionLabel(a) {
  if (!a) return "—";
  const first = String(a).split(":")[0];
  return ACTION_LABELS[a] || ACTION_LABELS[first] || a;
}

export default function AdminAudit() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [source, setSource] = useState("all");
  const [q, setQ] = useState("");
  const [detailId, setDetailId] = useState(null);

  const load = async () => {
    setLoading(true); setError("");
    try {
      const url = source === "all" ? "/admin/audit?limit=300" : `/admin/audit?limit=300&source=${source}`;
      const { data } = await api.get(url);
      setItems(data.items || []);
    } catch (e) { setError(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [source]);   // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return items;
    return items.filter((it) => {
      const hay = [
        it.actor_email, it.action, it.target_id, it.detail?.subject,
        it.detail?.requester_name, it.detail?.mediator_name,
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(s);
    });
  }, [items, q]);

  return (
    <div className="container-app py-8" data-testid="admin-audit-page">
      <AdminNav />
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="label-eyebrow text-amber-300">Administración</div>
          <h1 className="serif text-4xl mt-1 inline-flex items-center gap-2"><ScrollText size={26} className="text-amber-300"/> Auditoría</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            Historial de accesos a datos sensibles y cambios sobre mediaciones. Registro obligatorio conforme a la Ley N° 19.628 sobre protección de datos personales.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="btn-ghost text-xs py-1 px-2 border border-white/10 inline-flex items-center gap-1.5"><RefreshCw size={12}/> Refrescar</button>
          <button
            onClick={() => downloadCSV(
              `auditoria-colegalink-${new Date().toISOString().slice(0, 10)}.csv`,
              filtered,
              [
                { key: "at", label: "Fecha", format: (v) => (v ? new Date(v).toISOString() : "") },
                { key: "kind", label: "Origen" },
                { key: "actor_email", label: "Actor" },
                { key: "action", label: "Acción" },
                { key: "target_id", label: "ID objetivo" },
                { key: "target_kind", label: "Tipo objetivo" },
                { key: "target_role", label: "Rol objetivo" },
                { key: "detail", label: "Detalle", format: (v) => (v ? JSON.stringify(v) : "") },
              ],
            )}
            disabled={filtered.length === 0}
            className="btn-ghost text-xs py-1 px-2 border border-white/10 inline-flex items-center gap-1.5 disabled:opacity-40"
            data-testid="audit-export"
          >
            <Download size={12}/> Exportar CSV
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="mt-6 flex flex-wrap items-center gap-2">
        {[["all","Todo"],["access","Accesos a datos"],["mediation","Cambios en mediaciones"]].map(([k, label]) => (
          <button key={k} onClick={() => setSource(k)}
            className={`px-2.5 py-1 rounded-md text-xs border ${source === k ? "bg-cyan-400/10 border-cyan-400/40 text-cyan-100" : "border-white/10 text-muted-foreground hover:text-foreground"}`}
            data-testid={`audit-src-${k}`}
          >
            {label}
          </button>
        ))}
        <div className="relative ml-auto">
          <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground"/>
          <input
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar email, acción, ID…"
            className="field py-1 pl-7 pr-2 text-xs min-w-[260px]"
            data-testid="audit-search"
          />
        </div>
      </div>

      {error && <div className="mt-4 text-sm text-[hsl(var(--destructive))]">{error}</div>}

      {loading ? (
        <div className="mt-10 text-center text-muted-foreground"><Loader2 size={20} className="mx-auto animate-spin"/></div>
      ) : filtered.length === 0 ? (
        <div className="panel p-8 text-center mt-5" data-testid="audit-empty">
          <ScrollText size={26} className="mx-auto text-muted-foreground"/>
          <p className="mt-3 text-sm text-muted-foreground">Aún no hay eventos de auditoría registrados.</p>
        </div>
      ) : (
        <div className="panel overflow-hidden mt-5" data-testid="audit-table">
          <table className="w-full text-sm">
            <thead className="bg-white/[0.03] text-[11px] uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="text-left px-3 py-2 w-6"></th>
                <th className="text-left px-3 py-2">Fecha</th>
                <th className="text-left px-3 py-2">Origen</th>
                <th className="text-left px-3 py-2">Actor</th>
                <th className="text-left px-3 py-2">Acción</th>
                <th className="text-left px-3 py-2">Objetivo</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((ev, i) => {
                const rowId = `${ev.kind}-${ev.at}-${ev.target_id}-${i}`;
                const isOpen = detailId === rowId;
                const objectivo = ev.kind === "mediation"
                  ? (ev.detail?.subject || "Mediación") + (ev.detail?.mediator_name ? ` · ${ev.detail.mediator_name}` : "")
                  : (ev.target_role ? `${ev.target_role} · ${ev.target_id?.slice(0, 8)}` : ev.target_id?.slice(0, 8));
                return (
                  <tr key={rowId} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid="audit-row">
                    <td className="px-2 py-2 align-top">
                      <button onClick={() => setDetailId(isOpen ? null : rowId)} className="text-muted-foreground hover:text-foreground" aria-label="Detalle">
                        {isOpen ? <ChevronDown size={12}/> : <ChevronRight size={12}/>}
                      </button>
                    </td>
                    <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">{fmtDT(ev.at)}</td>
                    <td className="px-3 py-2">
                      {ev.kind === "access" ? (
                        <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-amber-400/10 border border-amber-400/40 text-amber-200">
                          <ShieldAlert size={10}/> Datos sensibles
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-cyan-400/10 border border-cyan-400/40 text-cyan-200">
                          <ScrollText size={10}/> Mediación
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs">{ev.actor_email || "—"}</td>
                    <td className="px-3 py-2">
                      <div className="inline-flex items-center gap-1">
                        {ev.kind === "access" ? <Eye size={11} className="text-amber-300"/> : <ArrowRight size={11} className="text-cyan-300"/>}
                        <span>{actionLabel(ev.action)}</span>
                      </div>
                      {isOpen && (
                        <div className="mt-2 text-[11px] text-muted-foreground font-mono bg-black/30 rounded p-2 max-w-2xl">
                          {JSON.stringify(ev, null, 2)}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">{objectivo}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
